import { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { searchEmails, type EmailSearchFilters } from '@/lib/inboxApi';
import { fetchLabels } from '@/lib/inboxLabelsApi';
import { InboxThreadPane } from '@/components/inbox/InboxThreadPane';
import { QueryErrorState } from '@/components/shared/QueryErrorState';

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (diffDays < 7) return d.toLocaleDateString('en-GB', { weekday: 'short' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function senderDisplay(from: string): string {
  const angleMatch = from.match(/^(.+?)\s*<[^>]+>$/);
  if (angleMatch && angleMatch[1]) return angleMatch[1].trim();
  return from;
}

export default function InboxSearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Pull query from URL so bookmarking / linking works
  const [q, setQ] = useState(searchParams.get('q') ?? '');
  const [committedQ, setCommittedQ] = useState(searchParams.get('q') ?? '');

  const [filters, setFilters] = useState<Partial<EmailSearchFilters>>({
    from: searchParams.get('from') ?? undefined,
    after: searchParams.get('after') ?? undefined,
    before: searchParams.get('before') ?? undefined,
    hasAttachment: searchParams.get('hasAttachment') === 'true' ? true : undefined,
    labelId: searchParams.get('labelId') ?? undefined,
  });

  const [showFilters, setShowFilters] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const effectiveFilters: EmailSearchFilters = {
    q: committedQ || undefined,
    ...filters,
    limit: 50,
  };
  const hasAnyFilter = Boolean(committedQ || filters.from || filters.after || filters.before ||
    filters.hasAttachment || filters.labelId);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['inbox-search', effectiveFilters],
    queryFn: () => searchEmails(effectiveFilters),
    enabled: hasAnyFilter,
  });

  const { data: labels = [] } = useQuery({
    queryKey: ['inbox-labels'],
    queryFn: fetchLabels,
  });

  function commitSearch() {
    const next = q.trim();
    setCommittedQ(next);
    const p: Record<string, string> = {};
    if (next) p['q'] = next;
    if (filters.from) p['from'] = filters.from;
    if (filters.after) p['after'] = filters.after;
    if (filters.before) p['before'] = filters.before;
    if (filters.hasAttachment) p['hasAttachment'] = 'true';
    if (filters.labelId) p['labelId'] = filters.labelId;
    setSearchParams(p, { replace: true });
  }

  function clearFilters() {
    setFilters({});
    setQ('');
    setCommittedQ('');
    setSearchParams({}, { replace: true });
    inputRef.current?.focus();
  }

  const results = data?.results ?? [];
  const total = data?.total ?? 0;

  return (
    <div style={{
      display: 'flex', height: '100vh',
      fontFamily: "'IBM Plex Sans', sans-serif",
      background: 'var(--surface-card)',
    }}>

      {/* Left pane — search + results */}
      <div style={{
        width: selectedEmailId ? 420 : '100%',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: selectedEmailId ? '1px solid var(--border-default)' : 'none',
        transition: 'width 0.15s',
        overflow: 'hidden',
      }}>

        {/* Search header */}
        <div style={{
          borderBottom: '1px solid var(--border-default)',
          padding: '16px 20px',
          background: 'var(--surface-card)',
        }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button
              onClick={() => navigate('/barrister/inbox')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', fontSize: 18, padding: 0, lineHeight: 1,
              }}
              title="Back to inbox"
            >
              ←
            </button>

            <div style={{
              flex: 1, display: 'flex', alignItems: 'center',
              border: '1px solid var(--border-strong)', borderRadius: 8,
              background: 'var(--surface)', overflow: 'hidden',
            }}>
              <span style={{ padding: '0 10px', color: 'var(--text-subtle)', fontSize: 15 }}>⌕</span>
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') commitSearch(); }}
                placeholder="Search emails…"
                autoFocus
                style={{
                  flex: 1, border: 'none', background: 'transparent',
                  padding: '9px 0', fontSize: 14, color: 'var(--text-primary)',
                  fontFamily: "'IBM Plex Sans', sans-serif", outline: 'none',
                }}
              />
              {q && (
                <button
                  onClick={() => { setQ(''); inputRef.current?.focus(); }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text-subtle)', fontSize: 16, padding: '0 10px',
                  }}
                >
                  ×
                </button>
              )}
            </div>

            <button
              onClick={commitSearch}
              style={{
                background: '#1e3a5f', color: 'white',
                border: 'none', borderRadius: 7,
                padding: '8px 16px', fontSize: 13, fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Search
            </button>

            <button
              onClick={() => setShowFilters((f) => !f)}
              style={{
                background: showFilters ? '#eff6ff' : 'none',
                color: showFilters ? '#2563EB' : '#6b7280',
                border: '1px solid var(--border-default)', borderRadius: 7,
                padding: '8px 12px', fontSize: 13, cursor: 'pointer',
              }}
            >
              Filters{Object.values(filters).some(Boolean) ? ' ●' : ''}
            </button>
          </div>

          {/* Filter panel */}
          {showFilters && (
            <div style={{
              marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end',
            }}>
              {/* From */}
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text-subtle)', marginBottom: 3 }}>
                  From
                </label>
                <input
                  value={filters.from ?? ''}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value || undefined }))}
                  placeholder="sender@example.com"
                  style={filterInputStyle}
                />
              </div>

              {/* After */}
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text-subtle)', marginBottom: 3 }}>
                  After
                </label>
                <input
                  type="date"
                  value={filters.after ?? ''}
                  onChange={(e) => setFilters((f) => ({ ...f, after: e.target.value || undefined }))}
                  style={filterInputStyle}
                />
              </div>

              {/* Before */}
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--text-subtle)', marginBottom: 3 }}>
                  Before
                </label>
                <input
                  type="date"
                  value={filters.before ?? ''}
                  onChange={(e) => setFilters((f) => ({ ...f, before: e.target.value || undefined }))}
                  style={filterInputStyle}
                />
              </div>

              {/* Label */}
              {labels.length > 0 && (
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--text-subtle)', marginBottom: 3 }}>
                    Label
                  </label>
                  <select
                    value={filters.labelId ?? ''}
                    onChange={(e) => setFilters((f) => ({ ...f, labelId: e.target.value || undefined }))}
                    style={{ ...filterInputStyle, paddingRight: 24 }}
                  >
                    <option value="">Any</option>
                    {labels.map((l) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Has attachment */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer', paddingBottom: 1 }}>
                <input
                  type="checkbox"
                  checked={filters.hasAttachment === true}
                  onChange={(e) => setFilters((f) => ({ ...f, hasAttachment: e.target.checked ? true : undefined }))}
                />
                Has attachment
              </label>

              {hasAnyFilter && (
                <button
                  onClick={clearFilters}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#dc2626', fontSize: 12, padding: '4px 0',
                  }}
                >
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>

        {/* Result count bar */}
        {hasAnyFilter && !isLoading && (
          <div style={{
            padding: '8px 20px',
            borderBottom: '1px solid var(--border-subtle)',
            fontSize: 12, color: 'var(--text-muted)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span>
              {isFetching ? 'Searching…' : total === 0 ? 'No results' : `${total.toLocaleString()} result${total !== 1 ? 's' : ''}`}
            </span>
            {committedQ && (
              <span>
                for <strong style={{ color: 'var(--text-primary)' }}>"{committedQ}"</strong>
              </span>
            )}
          </div>
        )}

        {/* Results list */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {!hasAnyFilter ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-subtle)' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>⌕</div>
              <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6 }}>
                Search your inbox
              </div>
              <div style={{ fontSize: 13 }}>
                Enter a keyword, sender, or use the filters to find emails.
              </div>
            </div>
          ) : isLoading ? (
            <div style={{ padding: 20 }}>
              {[...Array(8)].map((_, i) => (
                <div key={i} style={{
                  height: 60, background: 'var(--surface-sunken)', borderRadius: 6,
                  marginBottom: 8, animation: 'pulse 1.5s ease-in-out infinite',
                }} />
              ))}
            </div>
          ) : error ? (
            /* Already distinguished a failed search from "No emails found"; it just had no way
               to retry, so the only route back was to retype the search. */
            <QueryErrorState what="these search results" onRetry={() => void refetch()} />
          ) : results.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-subtle)' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✉</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-muted)', marginBottom: 6 }}>
                No emails found
              </div>
              <div style={{ fontSize: 13 }}>
                Try different keywords or adjust your filters.
              </div>
            </div>
          ) : (
            results.map((email) => {
              const isSelected = email.id === selectedEmailId;
              return (
                <div
                  key={email.id}
                  onClick={() => setSelectedEmailId(isSelected ? null : email.id)}
                  style={{
                    padding: '12px 20px',
                    borderBottom: '1px solid var(--border-subtle)',
                    cursor: 'pointer',
                    background: isSelected ? '#eff6ff' : email.isRead ? 'white' : '#f8faff',
                    borderLeft: isSelected ? '3px solid #2563EB' : '3px solid transparent',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
                    {!email.isRead && (
                      <div style={{
                        width: 7, height: 7, borderRadius: '50%',
                        background: '#2563EB', flexShrink: 0,
                      }} />
                    )}
                    <div style={{
                      flex: 1, fontSize: 13, fontWeight: email.isRead ? 400 : 600,
                      color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {senderDisplay(email.direction === 'outbound'
                        ? (email.toAddresses[0] ?? email.fromAddress)
                        : email.fromAddress)}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)', flexShrink: 0 }}>
                      {formatDate(email.receivedAt ?? email.sentAt)}
                    </div>
                  </div>

                  <div style={{
                    fontSize: 13, fontWeight: email.isRead ? 400 : 500,
                    color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    marginBottom: 2,
                  }}>
                    {email.subject}
                  </div>

                  {email.snippet && (
                    <div style={{
                      fontSize: 12, color: 'var(--text-subtle)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {email.snippet}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    {email.matter && (
                      <span style={{
                        fontSize: 11, color: '#059669',
                        background: '#ecfdf5', border: '1px solid #a7f3d0',
                        padding: '1px 6px', borderRadius: 4,
                      }}>
                        {email.matter.caseNumber}
                      </span>
                    )}
                    {email.hasAttachment && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>⊕ attachment</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right pane — thread view */}
      {selectedEmailId && (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{
            padding: '10px 16px', borderBottom: '1px solid var(--border-default)',
            display: 'flex', justifyContent: 'flex-end',
          }}>
            <button
              onClick={() => setSelectedEmailId(null)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', fontSize: 18, padding: 0, lineHeight: 1,
              }}
              title="Close"
            >
              ×
            </button>
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            <InboxThreadPane
              emailId={selectedEmailId}
              onArchive={() => setSelectedEmailId(null)}
              onFollowUp={() => { /* toast handled inside pane */ }}
              onLinkMatter={() => { /* link modal handled inside pane or inbox */ }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const filterInputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: "'IBM Plex Sans', sans-serif",
  color: 'var(--text-primary)',
  outline: 'none',
  // `backgroundColor`, not the `background` shorthand: the shorthand resets background-image to
  // none, which wipes the calendar icon globals.css paints on date inputs.
  backgroundColor: 'var(--surface-card)',
};
